import { useEffect, useMemo, useState } from 'react';
import type { Action } from '../../engine';
import { cleanName } from '../../worker/protocol';
import { useAssets } from '../assets';
import { Crest } from '../components/Art';
import { useToast } from '../components/Toasts';
import type { TableHandlers, TableModel } from '../game/types';
import { seatInfoFromView, useRoom } from '../game/useRoom';
import { navigate } from '../lib/router';
import { KEYS, load, remove, save } from '../lib/storage';
import { Lobby } from './Lobby';
import { TableScreen } from './TableScreen';

function NameGate({ code, onName }: { code: string; onName: (n: string) => void }) {
  const [name, setName] = useState('');
  const clean = cleanName(name);
  return (
    <main className="center-screen">
      <form
        className="panel gate"
        onSubmit={(e) => {
          e.preventDefault();
          if (clean) onName(clean);
        }}
      >
        <div className="gate-inner">
          <Crest width={110} />
          <h1 className="gate-title">You’re invited!</h1>
          <p>
            Game <b className="num">{code}</b> is waiting for you.
          </p>
          <label className="field">
            <span>Your name</span>
            <input autoFocus value={name} maxLength={20} placeholder="Sam" onChange={(e) => setName(e.target.value)} />
          </label>
          <button className="btn btn-gold btn-big wide-btn" type="submit" disabled={!clean}>
            Take a seat
          </button>
          <button type="button" className="link-btn ink" onClick={() => navigate({ name: 'home' })}>
            Back to home
          </button>
        </div>
      </form>
    </main>
  );
}

function Message({ title, body }: { title: string; body?: string }) {
  return (
    <main className="center-screen">
      <div className="panel gate">
        <div className="gate-inner">
          <h1 className="gate-title">{title}</h1>
          {body && <p>{body}</p>}
          <button className="btn btn-primary btn-big wide-btn" onClick={() => navigate({ name: 'home' })}>
            Back to home
          </button>
        </div>
      </div>
    </main>
  );
}

export function RoomScreen({ code }: { code: string }) {
  useAssets();
  const toast = useToast();
  const [name, setName] = useState<string | null>(() => cleanName(load(KEYS.name, '')) ?? null);
  const { client, snapshot, receivedAt, status, fatal, error, emotes } = useRoom(code, name);

  useEffect(() => {
    // A move that crossed with the server's update is just ignored; the table already shows the new state.
    if (!error || /not allowed right now/i.test(error.message)) return;
    toast(error.message, /watch/i.test(error.message) ? 'info' : 'error');
  }, [error, toast]);

  useEffect(() => {
    if (snapshot?.you !== null && snapshot?.you !== undefined) save(KEYS.lastRoom, { code, at: Date.now() });
  }, [snapshot?.you, code]);

  const handlers = useMemo<TableHandlers | null>(() => {
    if (!client) return null;
    return {
      act: (a: Action) => client.act(a),
      ready: () => client.ready(),
      forceNext: () => client.forceNext(),
      emote: (e) => client.emote(e),
      playAgain: () => client.start(snapshot?.state?.totalHoles ?? load(KEYS.onlineHoles, 9)),
      backToLobby: () => client.backToLobby(),
      home: () => navigate({ name: 'home' }),
      quit: () => {
        client.leave();
        remove(KEYS.lastRoom);
        navigate({ name: 'home' });
      },
    };
  }, [client, snapshot?.state?.totalHoles]);

  if (!name) {
    return (
      <NameGate
        code={code}
        onName={(n) => {
          save(KEYS.name, n);
          setName(n);
        }}
      />
    );
  }
  if (fatal === 'room-not-found') return <Message title={`No game called ${code}.`} body="Check the code." />;
  if (fatal === 'room-expired') return <Message title="That game has ended." />;
  if (fatal === 'bad-request') return <Message title="Something went wrong" body="We couldn’t join that game. Try again from home." />;
  if (!client || !handlers || !snapshot) {
    return (
      <main className="center-screen">
        <div className="finding">
          <span className="spinner" aria-hidden="true" />
          <p>{status === 'reconnecting' ? 'Reconnecting…' : 'Finding your table…'}</p>
        </div>
      </main>
    );
  }
  if (!snapshot.state) {
    return (
      <>
        <Lobby snap={snapshot} client={client} onHome={() => navigate({ name: 'home' })} />
        {status === 'reconnecting' && (
          <div className="conn-bar" role="status">
            <span className="spinner small" aria-hidden="true" /> Reconnecting…
          </div>
        )}
      </>
    );
  }
  const seats = snapshot.room.seats.map((v) => seatInfoFromView(v, receivedAt));
  const model: TableModel = {
    mode: 'online',
    state: snapshot.state,
    me: snapshot.you,
    isHost: snapshot.you !== null && snapshot.you === snapshot.host,
    hostSeat: snapshot.host,
    seats,
    roomCode: code,
    gameId: `room:${code}`,
  };
  return (
    <TableScreen
      model={model}
      handlers={handlers}
      incomingEmotes={emotes}
      connectionNote={status === 'reconnecting' || status === 'connecting' ? 'Reconnecting…' : null}
    />
  );
}
